/**
 * @fileoverview Source Document Preview
 *
 * Purpose: Embed the original uploaded precedent template (PDF or DOCX) inline
 * in the preview dialog. PDFs render via the in-house `PdfViewer` (pdfjs-dist
 * canvas renderer) so AcroForm OpenAction scripts (e.g. ATO Incap's
 * "Please save this document on your local desktop..." alert) never fire — the
 * Chrome native viewer would otherwise execute them. DOCX files render
 * client-side via the `docx-preview` library because Chrome cannot embed DOCX
 * in an iframe (it would force a download instead).
 *
 * Used by: PreviewDialog "Original" tab in /dashboard/settings/docs.
 */

import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { PdfViewer } from "@/ui/composed/data-display";

interface SourceDocPreviewProps {
  /** Signed Convex storage URL for the source document */
  url: string;
  /**
   * Original filename — used to detect the file format. We can't rely on the
   * URL extension because Convex storage URLs are opaque IDs.
   */
  fileName?: string | null;
  /** Accessible title for the embedded frame */
  title?: string;
}

function getExtension(fileName?: string | null, url?: string): string {
  const candidate = (fileName ?? url ?? "").toLowerCase();
  const cleaned = candidate.split("?")[0]?.split("#")[0] ?? "";
  const dot = cleaned.lastIndexOf(".");
  return dot >= 0 ? cleaned.slice(dot + 1) : "";
}

export function SourceDocPreview({
  url,
  fileName,
  title,
}: SourceDocPreviewProps) {
  const ext = getExtension(fileName, url);
  const isPdf = ext === "pdf";
  const isDocx =
    ext === "docx" || ext === "doc" || ext === "dotx" || ext === "dot";

  if (isPdf) {
    return (
      <div className="h-full w-full" style={{ minHeight: "80vh" }}>
        <PdfViewer url={url} title={title ?? "Original document"} />
      </div>
    );
  }

  if (isDocx) {
    return <DocxRenderer url={url} title={title} />;
  }

  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="max-w-md text-sm text-muted-foreground">
        This file type can't be previewed inline. Use the Download button in the
        header to save it locally.
      </p>
    </div>
  );
}

/**
 * Renders a DOCX blob client-side using the `docx-preview` library. The
 * library renders into a container element, so we manage the DOM target via a
 * ref and clear it between document changes. The library is dynamically
 * imported to keep it out of the main bundle.
 */
function DocxRenderer({ url, title }: { url: string; title?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = "";
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const { renderAsync } = await import("docx-preview");
        if (cancelled || !containerRef.current) return;
        await renderAsync(blob, containerRef.current, undefined, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          experimental: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          // Word's <w:altChunk> embeds external HTML inside a DOCX. Without
          // this flag docx-preview silently drops them, which (for documents
          // composed mostly of altChunks) leaves the page looking nearly
          // empty even though Word renders it fully.
          renderAltChunks: true,
          className: "docx",
        });
        if (cancelled) return;
        // docx-preview renders altChunks as <iframe srcdoc="..."> elements,
        // which get the browser's default tiny iframe size (~300×150) instead
        // of the page width — and the iframe also blocks the surrounding
        // text from flowing around/under it. Replace each altChunk iframe
        // with its inlined body content so the embedded HTML behaves like
        // first-class document content.
        inlineAltChunkIframes(containerRef.current);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Unknown error";
          setError(message);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div
      className="relative h-full w-full overflow-auto bg-muted/30"
      style={{ minHeight: "80vh" }}
      aria-label={title ?? "Original document"}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading document…
        </div>
      )}
      {error && (
        <div className="p-6 text-sm">
          <p className="text-destructive">Could not render document: {error}</p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-primary underline-offset-2 hover:underline"
          >
            Open in a new tab
          </a>
        </div>
      )}
      <div ref={containerRef} className="docx-source-preview p-4" />
    </div>
  );
}

/**
 * Replace `<iframe srcdoc="…">` elements (which docx-preview uses for
 * `<w:altChunk>` parts) with their inlined body content so the embedded HTML
 * flows inside the rendered DOCX page instead of being squeezed into the
 * iframe's tiny default viewport.
 *
 * IMPORTANT: We deliberately skip `<style>` tags from the altChunk document.
 * `<style>` elements apply globally regardless of where they're inserted in
 * the DOM, so cloning them into the preview would bleed altChunk CSS (often
 * `body { font-family: … }` and other generic rules) into the surrounding
 * dialog and change the app's typography. The altChunk's content-level
 * formatting is already carried by inline styles on the elements themselves.
 */
function inlineAltChunkIframes(root: HTMLElement) {
  const iframes = root.querySelectorAll<HTMLIFrameElement>("iframe[srcdoc]");
  for (const iframe of Array.from(iframes)) {
    const html = iframe.srcdoc;
    if (!html) {
      iframe.remove();
      continue;
    }
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const wrapper = document.createElement("div");
    wrapper.className = "docx-altchunk-inline";
    Array.from(parsed.body.childNodes).forEach((node) => {
      const cloned = node.cloneNode(true);
      // Defensive: strip any nested <style>/<link> nodes that ended up inside
      // <body> rather than <head>.
      if (cloned instanceof HTMLElement) {
        cloned
          .querySelectorAll("style, link[rel='stylesheet']")
          .forEach((el) => el.remove());
      }
      wrapper.appendChild(cloned);
    });
    iframe.replaceWith(wrapper);
  }
}
